import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Settings, Cast } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ThemedIcon, ControlsTheme } from './ThemedIcon';

interface SettingsMenuProps {
  speedEnabled: boolean;
  currentSpeed: number;
  onSpeedChange: (speed: number) => void;
  pipEnabled: boolean;
  onTogglePip: () => void;
  castEnabled: boolean;
  onTvCast?: () => void;
  fullscreenEnabled: boolean;
  onToggleFullscreen: () => void;
  color?: string;
  theme?: ControlsTheme;
}

const SPEED_OPTIONS = [
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, get label() { return translateInterfaceText("Normal"); } },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 2, label: '2x' },
];

export const SettingsMenu: React.FC<SettingsMenuProps> = ({
  speedEnabled,
  currentSpeed,
  onSpeedChange,
  pipEnabled,
  onTogglePip,
  castEnabled,
  onTvCast,
  fullscreenEnabled,
  onToggleFullscreen,
  color,
  theme = 'minimal',
}) => {
  useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSpeed, setActiveSpeed] = useState<number>(currentSpeed);

  useEffect(() => {
    setActiveSpeed(currentSpeed);
  }, [currentSpeed]);

  const activeSpeedLabel =
    SPEED_OPTIONS.find((o) => o.value === activeSpeed)?.label || 'Normal';

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0 hover:bg-white/20"
          style={color ? { color } : undefined}
          aria-label={translateInterfaceText("Configurações")}
        >
          <Settings className="h-5 w-5" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[200px] bg-black/90 border-white/20 text-white"
      >
        {speedEnabled && (
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="text-white hover:bg-white/20 focus:bg-white/20 data-[state=open]:bg-white/20 cursor-pointer">
              <ThemedIcon name="gauge" theme={theme} className="h-4 w-4 mr-2" />
              <span className="flex-1">{translateInterfaceText("Velocidade")}</span>
              <span className="ml-2 text-xs opacity-70">{activeSpeedLabel}</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent className="min-w-[110px] bg-black/90 border-white/20 text-white">
                {SPEED_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.value}
                    onClick={() => {
                      onSpeedChange(opt.value);
                      setActiveSpeed(opt.value);
                      setIsOpen(false);
                    }}
                    className={`text-white hover:bg-white/20 focus:bg-white/20 cursor-pointer justify-center ${
                      activeSpeed === opt.value ? 'bg-white/10' : ''
                    }`}
                  >
                    {opt.label}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>
        )}

        {pipEnabled && (
          <DropdownMenuItem
            onClick={() => {
              onTogglePip();
              setIsOpen(false);
            }}
            className="text-white hover:bg-white/20 focus:bg-white/20 cursor-pointer"
          >
            <ThemedIcon name="pip" theme={theme} className="h-4 w-4 mr-2" />
            <span>{translateInterfaceText("Tela flutuante")}</span>
          </DropdownMenuItem>
        )}

        {castEnabled && onTvCast && (
          <DropdownMenuItem
            onClick={() => {
              onTvCast();
              setIsOpen(false);
            }}
            className="text-white hover:bg-white/20 focus:bg-white/20 cursor-pointer"
          >
            <Cast className="h-4 w-4 mr-2" />
            <span>{translateInterfaceText("Transmitir para TV")}</span>
          </DropdownMenuItem>
        )}

        {fullscreenEnabled && (
          <DropdownMenuItem
            onClick={() => {
              onToggleFullscreen();
              setIsOpen(false);
            }}
            className="text-white hover:bg-white/20 focus:bg-white/20 cursor-pointer"
          >
            <ThemedIcon name="fullscreen" theme={theme} className="h-4 w-4 mr-2" />
            <span>{translateInterfaceText("Tela cheia")}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
